import './utils/setupErrorToast';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntdApp } from 'antd';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/Layout/AppLayout';
import BessLayout from './components/Layout/BessLayout';
import DsmMinLayout from './components/Layout/DsmMinLayout';
import LoginPage from './pages/LoginPage';
import ExplorerDashboard from './pages/ExplorerDashboard';
import TradingDashboard from './pages/TradingDashboard';
import DsmAnalysisDashboard from './pages/DsmAnalysisDashboard';
import AppSelectionPage from './pages/AppSelectionPage';
import MarketDataDashboard from './pages/MarketDataDashboard';
import RevenueDashboard from './pages/RevenueDashboard';
import WhatIfPage from './pages/WhatIfPage';
import MonthlyReportPage from './pages/MonthlyReportPage';
import WeeklyReportPage from './pages/WeeklyReportPage';
import UserManagementPage from './pages/UserManagementPage';

// BESS pages
import BessSizingPage from './pages/bess-sizing/BessSizingPage';
import BessSizingResultPage from './pages/bess-sizing/BessSizingResultPage';
import HybridBessPage from './pages/hybrid-bess/HybridBessPage';
import HybridBessResultPage from './pages/hybrid-bess/HybridBessResultPage';
import HybridBessWindPage from './pages/hybrid-bess-wind/HybridBessWindPage';
import BessContainerPage from './pages/bess-container/BessContainerPage';
import PcsPage from './pages/pcs/PcsPage';

// DSM Minimization pages
import DsmMinDashboardPage from './pages/dsm-min/DsmMinDashboardPage';
import DsmMinIngestionPage from './pages/dsm-min/DsmMinIngestionPage';
import DsmMinRawDataPage from './pages/dsm-min/DsmMinRawDataPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  return user ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  const canManage = Boolean(user?.is_admin || user?.is_staff || user?.is_superuser);
  return canManage ? children : <Navigate to="/revenue" replace />;
}

function AppRoute({ appId, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" />;

  const isSuper = Boolean(user?.is_superuser);
  const allowed = user?.allowed_apps;

  const hasAccess = isSuper || (Array.isArray(allowed) && allowed.includes(appId));

  if (!hasAccess && Array.isArray(allowed)) {
    return <Navigate to="/app-selection" replace />;
  }
  return children;
}

export default function App() {
  return (
    <ConfigProvider theme={{ token: { fontFamily: 'Inter, sans-serif', colorPrimary: '#0052CC', borderRadius: 8 } }}>
      <AntdApp>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/app-selection" element={<ProtectedRoute><AppSelectionPage /></ProtectedRoute>} />

            {/* Trading App */}
            <Route path="/" element={<ProtectedRoute><AppRoute appId="trading"><AppLayout /></AppRoute></ProtectedRoute>}>
              <Route index element={<Navigate to="/revenue" replace />} />
              <Route path="market" element={<MarketDataDashboard />} />
              <Route path="revenue" element={<RevenueDashboard />} />
              <Route path="volume" element={<TradingDashboard />} />
              <Route path="dsm" element={<DsmAnalysisDashboard />} />
              <Route path="explorer" element={<ExplorerDashboard />} />
              <Route path="what-if" element={<WhatIfPage />} />
              <Route path="weekly-report" element={<WeeklyReportPage />} />
              <Route path="monthly-report" element={<MonthlyReportPage />} />
              <Route path="user-management" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
            </Route>

            {/* BESS Sizing App */}
            <Route element={<ProtectedRoute><AppRoute appId="bess"><BessLayout /></AppRoute></ProtectedRoute>}>
              <Route path="bess-sizing" element={<BessSizingPage />} />
              <Route path="bess-sizing/:id" element={<BessSizingResultPage />} />
              <Route path="hybrid-bess" element={<HybridBessPage />} />
              <Route path="hybrid-bess/history" element={<HybridBessPage defaultTab="history" />} />
              <Route path="hybrid-bess/:id" element={<HybridBessResultPage />} />
              <Route path="hybrid-bess-wind" element={<HybridBessWindPage />} />
              <Route path="hybrid-bess-wind/history" element={<HybridBessWindPage defaultTab="history" />} />
              <Route path="hybrid-bess-wind/:id" element={<HybridBessWindPage />} />
              <Route path="bess-container" element={<BessContainerPage />} />
              <Route path="pcs" element={<PcsPage />} />
            </Route>

            {/* DSM Minimization App */}
            <Route element={<ProtectedRoute><AppRoute appId="dsm_minimization"><DsmMinLayout /></AppRoute></ProtectedRoute>}>
              <Route path="dsm-minimization" element={<DsmMinDashboardPage />} />
              <Route path="dsm-minimization/raw-data" element={<DsmMinRawDataPage />} />
              <Route path="dsm-minimization/ingestion" element={<DsmMinIngestionPage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
}
